// Pure aggregation model for the Pipeline Activity tab.
//
// Centralizes the milestone-completion flattening that used to live inline in
// PipelineActivityReport, and adds the activity-perspective rollups:
//   - productivity: who moved how many loans (per employee + per role)
//   - velocity:     time-in-stage from consecutive stages' startDates
//   - volume:       actions per Pacific day/month + per-milestone counts
//   - stalled:      the Loan Center "stalled" flag, via the shared
//                   loanStaleness service (rescue-only milestone_date basis,
//                   independent of lock timing, 7 Pacific days) — as of today
//
// Reuses the Line Report's Pacific-pinned axis (buildTimeAxis) and the shared
// helpers inferRole / derivePipelineStatus / normalizeLoKey / loanStaleness.
import { parseISO } from 'date-fns';
import {
  PipelineLoan,
  PipelineStatus,
  MILESTONE_TO_STATUS,
  STATUS_CONFIG,
  inferRole,
  derivePipelineStatus,
  getLoanOfficerName,
  getMilestoneDoneBy,
  getMilestoneDoneDate
} from '../types';
import { STATUS_LINE_CONFIG, buildTimeAxis, type TimeAxis } from './pipelineLineModel';
import { getBusinessDateString, diffCalendarDays, toLocalDateString } from '@/utils/dateHelpers';
import { normalizeLoKey } from '../loScoreboard';
import { isStalled, getDaysStale } from '@/services/loanStaleness';
import { getCanonicalEventDate, toPacificDateKey } from '../reporting';

// Known Encompass doneBy misspellings (mirrors the prior inline map).
const NAME_CORRECTIONS: Record<string, string> = {
  'Mary Levya': 'Mary Leyva'
};

const UNASSIGNED = 'Unassigned';

const STATUS_META = new Map(STATUS_LINE_CONFIG.map((c) => [c.status, c]));
const STATUS_INDEX = new Map(STATUS_LINE_CONFIG.map((c, i) => [c.status, i]));

export interface ActivityEvent {
  doneBy: string; // display name (NAME_CORRECTIONS applied); '' when blank
  role: string;
  status: PipelineStatus;
  loan: PipelineLoan;
  doneDate: string;
  instant: Date;
  ptDay: string; // Pacific YYYY-MM-DD
  ptMonth: string; // Pacific YYYY-MM
}

/**
 * Flatten each loan's milestone_history into per-action events within the
 * Pacific-pinned window. Requires `done` + `doneDate`; a blank `doneBy` is
 * KEPT (attributed via inferRole's milestone fallback) so productivity tallies
 * stay complete.
 */
export function flattenActivityEvents(
  loans: PipelineLoan[],
  range: { from: Date; to: Date }
): ActivityEvent[] {
  const rangeStart = toLocalDateString(range.from);
  const rangeEnd = toLocalDateString(range.to);
  const events: ActivityEvent[] = [];
  for (const loan of loans) {
    for (const cfg of STATUS_LINE_CONFIG) {
      const milestoneDate = getMilestoneDoneDate(loan, cfg.status);
      const canonicalDate = cfg.status === 'funded'
        ? getCanonicalEventDate(loan, 'funded')
        : cfg.status === 'purchased'
          ? getCanonicalEventDate(loan, 'purchased')
          : null;
      const usesCanonicalDate = cfg.status === 'funded' || cfg.status === 'purchased';
      const doneDate = usesCanonicalDate ? canonicalDate : milestoneDate;
      const ptDay = canonicalDate || toPacificDateKey(milestoneDate);
      if (!doneDate || !ptDay || ptDay < rangeStart || ptDay > rangeEnd) {
        continue;
      }
      const instant = parseISO(
        /^\d{4}-\d{2}-\d{2}$/.test(doneDate)
          ? `${doneDate}T12:00:00`
          : doneDate
      );
      if (isNaN(instant.getTime())) {
        continue;
      }
      const cleaned = (getMilestoneDoneBy(loan, cfg.status) || '').replace(/\s+/g, ' ').trim();
      const doneBy = NAME_CORRECTIONS[cleaned] || cleaned;
      events.push({
        doneBy,
        role: inferRole(doneBy, loan, cfg.label),
        status: cfg.status,
        loan,
        doneDate,
        instant,
        ptDay,
        ptMonth: ptDay.slice(0, 7)
      });
    }
  }
  // Newest first (default feed order).
  events.sort((a, b) => b.instant.getTime() - a.instant.getTime());
  return events;
}

/** Distinct loans represented in an event set (for velocity scope). */
export function distinctLoans(events: ActivityEvent[]): PipelineLoan[] {
  const seen = new Map<string, PipelineLoan>();
  for (const ev of events) {
    if (!seen.has(ev.loan.encompass_loan_id)) {
      seen.set(ev.loan.encompass_loan_id, ev.loan);
    }
  }
  return [...seen.values()];
}

// ── Productivity ───────────────────────────────────────────────────────────

export interface ProductivityRow {
  key: string;
  displayName: string;
  role: string; // modal role across this person's actions
  actions: number;
  loans: number; // distinct loans touched
  volume: number; // sum over distinct loans (avoids multi-milestone double count)
}

export interface RoleRow {
  role: string;
  actions: number;
  loans: number;
  volume: number;
}

function modalRole(counts: Map<string, number>): string {
  let best = '';
  let bestN = -1;
  for (const [role, n] of counts) {
    if (n > bestN) {
      best = role;
      bestN = n;
    }
  }
  return best || 'Operations';
}

function sumValues(m: Map<string, number>): number {
  let s = 0;
  for (const v of m.values()) {
    s += v;
  }
  return s;
}

export function rollupProductivity(events: ActivityEvent[]): {
  rows: ProductivityRow[];
  byRole: RoleRow[];
} {
  interface PersonAcc {
    displayName: string;
    roleCounts: Map<string, number>;
    actions: number;
    loanIds: Set<string>;
    volumeByLoan: Map<string, number>;
  }
  interface RoleAcc {
    actions: number;
    loanIds: Set<string>;
    volumeByLoan: Map<string, number>;
  }
  const byPerson = new Map<string, PersonAcc>();
  const byRoleAcc = new Map<string, RoleAcc>();

  for (const ev of events) {
    const norm = normalizeLoKey(ev.doneBy);
    // Blank actors group under a sentinel key/display, never an empty key.
    const key = norm || 'unassigned';
    const display = norm ? ev.doneBy : UNASSIGNED;
    const loanId = ev.loan.encompass_loan_id;
    const amount = ev.loan.trans_details_loan_amt || 0;

    let rec = byPerson.get(key);
    if (!rec) {
      rec = {
        displayName: display,
        roleCounts: new Map(),
        actions: 0,
        loanIds: new Set(),
        volumeByLoan: new Map()
      };
      byPerson.set(key, rec);
    }
    rec.actions += 1;
    rec.roleCounts.set(ev.role, (rec.roleCounts.get(ev.role) || 0) + 1);
    rec.loanIds.add(loanId);
    rec.volumeByLoan.set(loanId, amount);

    let rr = byRoleAcc.get(ev.role);
    if (!rr) {
      rr = { actions: 0, loanIds: new Set(), volumeByLoan: new Map() };
      byRoleAcc.set(ev.role, rr);
    }
    rr.actions += 1;
    rr.loanIds.add(loanId);
    rr.volumeByLoan.set(loanId, amount);
  }

  const rows = [...byPerson.entries()]
    .map(([key, rec]) => ({
      key,
      displayName: rec.displayName,
      role: modalRole(rec.roleCounts),
      actions: rec.actions,
      loans: rec.loanIds.size,
      volume: sumValues(rec.volumeByLoan)
    }))
    .sort(
      (a, b) =>
        b.actions - a.actions ||
        b.volume - a.volume ||
        a.displayName.localeCompare(b.displayName)
    );

  const byRole = [...byRoleAcc.entries()]
    .map(([role, rr]) => ({
      role,
      actions: rr.actions,
      loans: rr.loanIds.size,
      volume: sumValues(rr.volumeByLoan)
    }))
    .sort((a, b) => b.actions - a.actions);

  return { rows, byRole };
}

// ── Velocity (time-in-stage) ─────────────────────────────────────────────────

export interface VelocityRow {
  fromStatus: PipelineStatus;
  toStatus: PipelineStatus;
  fromLabel: string;
  toLabel: string;
  adjacent: boolean; // false when the observed transition skips a canonical stage
  n: number;
  medianDays: number;
  avgDays: number;
}

// Reached stages (done OR current in-progress carry startDate) as the earliest
// startDate per canonical status, ordered by pipeline position.
function stageEntries(loan: PipelineLoan): { status: PipelineStatus; ptDay: string }[] {
  if (!loan.milestone_history) {
    return [];
  }
  const earliest = new Map<PipelineStatus, string>();
  for (const m of loan.milestone_history) {
    if (!m.startDate) {
      continue; // only reached stages carry a startDate
    }
    const status = MILESTONE_TO_STATUS[m.name.toLowerCase().trim()];
    if (!status) {
      continue;
    }
    const prev = earliest.get(status);
    if (!prev || m.startDate < prev) {
      earliest.set(status, m.startDate);
    }
  }
  return [...earliest.entries()]
    .sort(
      (a, b) => (STATUS_INDEX.get(a[0]) ?? 0) - (STATUS_INDEX.get(b[0]) ?? 0)
    )
    .map(([status, startDate]) => ({
      status,
      ptDay: getBusinessDateString(parseISO(startDate))
    }));
}

function median(arr: number[]): number {
  if (arr.length === 0) {
    return 0;
  }
  const s = [...arr].sort((x, y) => x - y);
  const n = s.length;
  return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function rollupVelocity(loans: PipelineLoan[]): VelocityRow[] {
  interface PairAcc {
    fromStatus: PipelineStatus;
    toStatus: PipelineStatus;
    adjacent: boolean;
    durations: number[];
  }
  const byPair = new Map<string, PairAcc>();
  for (const loan of loans) {
    const stages = stageEntries(loan);
    for (let i = 0; i + 1 < stages.length; i++) {
      const a = stages[i];
      const b = stages[i + 1];
      const days = diffCalendarDays(a.ptDay, b.ptDay);
      // Ignore non-positive diffs: negatives are out-of-order anomalies, and
      // 0-day transitions are typically co-stamped stage entries at file
      // creation (startDate ~= doneDate), which would deflate the metric.
      if (days <= 0) {
        continue;
      }
      const fi = STATUS_INDEX.get(a.status) ?? 0;
      const ti = STATUS_INDEX.get(b.status) ?? 0;
      const key = `${a.status}>${b.status}`;
      let acc = byPair.get(key);
      if (!acc) {
        acc = {
          fromStatus: a.status,
          toStatus: b.status,
          adjacent: ti - fi === 1,
          durations: []
        };
        byPair.set(key, acc);
      }
      acc.durations.push(days);
    }
  }
  return [...byPair.values()]
    .map((acc) => ({
      fromStatus: acc.fromStatus,
      toStatus: acc.toStatus,
      fromLabel: STATUS_META.get(acc.fromStatus)?.label ?? acc.fromStatus,
      toLabel: STATUS_META.get(acc.toStatus)?.label ?? acc.toStatus,
      adjacent: acc.adjacent,
      n: acc.durations.length,
      medianDays: round1(median(acc.durations)),
      avgDays: round1(
        acc.durations.reduce((s, v) => s + v, 0) / acc.durations.length
      )
    }))
    .sort(
      (a, b) =>
        (STATUS_INDEX.get(a.fromStatus) ?? 0) -
          (STATUS_INDEX.get(b.fromStatus) ?? 0) ||
        (STATUS_INDEX.get(a.toStatus) ?? 0) - (STATUS_INDEX.get(b.toStatus) ?? 0)
    );
}

// ── Volume over time ─────────────────────────────────────────────────────────

export interface VolumePoint {
  key: string;
  label: string;
  count: number;
}

export interface StatusCount {
  status: PipelineStatus;
  label: string;
  color: string;
  count: number;
}

export function rollupVolume(
  events: ActivityEvent[],
  axis: TimeAxis
): { points: VolumePoint[]; byStatus: StatusCount[] } {
  const countByKey = new Map<string, number>();
  const byStatusMap = new Map<PipelineStatus, number>();
  for (const ev of events) {
    const key = axis.granularity === 'daily' ? ev.ptDay : ev.ptMonth;
    countByKey.set(key, (countByKey.get(key) || 0) + 1);
    byStatusMap.set(ev.status, (byStatusMap.get(ev.status) || 0) + 1);
  }
  const points = axis.keys.map((k) => ({
    key: k,
    label: axis.labelFor(k),
    count: countByKey.get(k) || 0
  }));
  const byStatus = STATUS_LINE_CONFIG.map((c) => ({
    status: c.status,
    label: c.label,
    color: c.color,
    count: byStatusMap.get(c.status) || 0
  })).filter((s) => s.count > 0);
  return { points, byStatus };
}

// ── Stalled (shared Loan Center calc — as of today) ──────────────────────────

export interface StalledRow {
  loan: PipelineLoan;
  stage: PipelineStatus;
  stageLabel: string;
  ownerName: string;
  ownerRole: string;
  daysStale: number;
}

function fullName(
  first: string | null | undefined,
  last: string | null | undefined
): string {
  return [first, last].filter(Boolean).join(' ').trim();
}

type OwnerRole = 'Loan Officer' | 'Processor' | 'Underwriter' | 'Closer';

function stageToRole(stage: PipelineStatus): OwnerRole {
  switch (stage) {
    case 'started':
    case 'loan_setup':
      return 'Loan Officer';
    case 'processing':
    case 'submitted':
    case 'resubmitted':
      return 'Processor';
    case 'cond_approval':
    case 'final_approval':
      return 'Underwriter';
    case 'docs_out':
    case 'funded':
    case 'purchased':
    default:
      return 'Closer';
  }
}

function resolveStaleOwner(
  loan: PipelineLoan,
  stage: PipelineStatus
): { ownerName: string; ownerRole: string } {
  const role = stageToRole(stage);
  let name = '';
  switch (role) {
    case 'Loan Officer':
      name = getLoanOfficerName(loan);
      break;
    case 'Processor':
      name = fullName(loan.loan_processor_first_name, loan.loan_processor_last_name);
      break;
    case 'Underwriter':
      name = fullName(loan.underwriter_first_name, loan.underwriter_last_name);
      break;
    case 'Closer':
      name = fullName(loan.closer_first_name, loan.closer_last_name);
      break;
  }
  if (!name || name === '-') {
    name = getLoanOfficerName(loan); // fall back to the LO
  }
  if (!name || name === '-') {
    name = UNASSIGNED;
  }
  return { ownerName: name, ownerRole: role };
}

// The stage a loan is currently *sitting* in: the furthest-reached milestone
// with a startDate (done OR the current in-progress one). derivePipelineStatus
// only sees completed milestones, so it would report the last done stage for a
// loan parked in an in-progress milestone.
function currentReachedStage(loan: PipelineLoan): PipelineStatus | null {
  const stages = stageEntries(loan); // ordered by pipeline position
  return stages.length ? stages[stages.length - 1].status : null;
}

/**
 * Stalled loans as of today, using the shared Loan Center calc
 * (`loanStaleness.isStalled`): rescue-only `milestone_date` basis, independent
 * of lock timing, with a 7-Pacific-day threshold. Report-only view —
 * independent of the date range, honoring only the parent entity filters
 * (baked into `loans`). Active loans only (funded/purchased excluded, keyed
 * off the completed-milestone status).
 */
export function computeStalledLoans(loans: PipelineLoan[]): StalledRow[] {
  const rows: StalledRow[] = [];
  for (const loan of loans) {
    if (loan.loan_folder !== 'Active') {
      continue;
    }
    const completedStatus = derivePipelineStatus(loan);
    if (
      completedStatus === 'funded'
      || completedStatus === 'purchased'
      || getCanonicalEventDate(loan, 'funded')
      || getCanonicalEventDate(loan, 'purchased')
    ) {
      continue; // active loans only
    }
    if (!isStalled(loan)) {
      continue;
    }
    // Display + owner reflect the stage the loan is actually sitting in (the
    // current in-progress milestone), not the last completed one.
    const stage = currentReachedStage(loan) ?? completedStatus;
    const { ownerName, ownerRole } = resolveStaleOwner(loan, stage);
    rows.push({
      loan,
      stage,
      stageLabel: STATUS_CONFIG[stage]?.label ?? stage,
      ownerName,
      ownerRole,
      daysStale: getDaysStale(loan) ?? 0
    });
  }
  return rows.sort((a, b) => b.daysStale - a.daysStale);
}

// ── Orchestrator (used by tests + initial render) ────────────────────────────

export interface ActivityModel {
  events: ActivityEvent[];
  productivity: { rows: ProductivityRow[]; byRole: RoleRow[] };
  velocity: VelocityRow[];
  volume: { points: VolumePoint[]; byStatus: StatusCount[] };
  stalled: StalledRow[];
  axis: TimeAxis;
}

export function buildActivityModel(
  loans: PipelineLoan[],
  range: { from: Date; to: Date }
): ActivityModel {
  const axis = buildTimeAxis(range);
  const events = flattenActivityEvents(loans, range);
  return {
    events,
    productivity: rollupProductivity(events),
    velocity: rollupVelocity(distinctLoans(events)),
    volume: rollupVolume(events, axis),
    stalled: computeStalledLoans(loans),
    axis
  };
}
